import * as React from "react"
import LoadingSpinner from "../components/root/LoadingSpinner"
import addLayers from "../utils/AddLayers"
import StoryCreator from "../utils/storyCreator"
import { useSocket } from "./socket-context"
import { useStoryData } from "./story-data"

export const isBrowser = () => typeof window !== "undefined"

const StoryContext = React.createContext()

export const StoryProvider = ({ children, ...props }) => {
  const { loadingStoryData, positionObtained, skipPositionObtain, storyData, submitted } =
    useStoryData()
  const mapRef = React.useRef()
  const webMapRef = React.useRef()
  const travelTimeout = React.useRef(null)
  const rotateTimeout = React.useRef(null)
  const [mapLoaded, setMapLoaded] = React.useState(false)
  const [storyEnd, setStoryEnd] = React.useState(false)
  const [ready, setReady] = React.useState(false)
  const [rotate, setRotate] = React.useState(true)
  const [showTools, setShowTools] = React.useState(false)
  const [storyIndex, setStoryIndex] = React.useState(0)
  const [storyItems, setStoryItems] = React.useState([
    {
      title: "StoryStub",
      body: "This is the content of my story block.",
      goTo: {
        target: [-3.0516, 52.7263],
        zoom: 1,
        duration: 2000,
      },
      rotate: false,
      layers: [],
      points: [],
      lines: [],
    },
  ])

  React.useEffect(() => {
    const newStoryItems = StoryCreator(storyData)
    newStoryItems.length > 0 && setStoryItems(newStoryItems)
  }, [storyData])

  React.useEffect(() => {
    if (ready && showTools) {
      document.body.classList.remove("tools-hidden")
    } else {
      document.body.classList.add("tools-hidden")
    }
  }, [ready, showTools])

  React.useEffect(() => {
    if (ready) {
      document.body.classList.add("noscroll")
    } else {
      document.body.classList.remove("noscroll")
    }
  }, [ready])

  React.useEffect(() => {
    if (ready) {
      setRotate(false)
      travelTimeout.current = setTimeout(() => {
        webMapRef.current.layers.removeAll()
        if (storyItems[storyIndex].goTo) {
          const { target, zoom, duration } = storyItems[storyIndex].goTo
          mapRef.current.goTo(
            {
              target: target,
              zoom: zoom,
            },
            { animate: true, duration: duration }
          )
          if (storyItems[storyIndex].rotate === true) {
            rotateTimeout.current = setTimeout(() => {
              setRotate(true)
            }, duration)
          }
        } else {
          if (storyItems[storyIndex].rotate === true) {
            setRotate(true)
          }
        }

        if (
          storyItems[storyIndex].layers &&
          storyItems[storyIndex].layers.length !== 0
        ) {
          addLayers(webMapRef, storyItems[storyIndex].layers)
        }
      }, 200)
    }
    return () => {
      clearInterval(travelTimeout.current)
      clearInterval(rotateTimeout.current)
    }
  }, [ready, storyIndex, storyItems])

  const next = () => {
    if (storyIndex === storyItems.length - 1) {
      setStoryEnd(true)
    }
    setStoryIndex(Math.min(storyIndex + 1, storyItems.length - 1))
  }
  const prev = () => {
    setStoryIndex(Math.max(storyIndex - 1, 0))
  }
  const hasNext = storyIndex + 1 <= storyItems.length - 1
  const hasPrev = storyIndex - 1 >= 0

  React.useEffect(() => {
    if (!loadingStoryData && positionObtained && mapLoaded) {
      setReady(true)
    }
  }, [loadingStoryData, positionObtained, mapLoaded])

  return (
    <StoryContext.Provider
      value={{
        toggleTools: () => setShowTools(!showTools),
        mapRef,
        webMapRef,
        mapLoaded,
        setMapLoaded,
        ready,
        currentStoryItem: storyItems[storyIndex],
        next,
        prev,
        hasNext,
        hasPrev,
        storyEnd,
        setStoryEnd,
        rotate,
        auditScores: storyData.auditScores,
      }}
      {...props}
    >
      {(loadingStoryData || !positionObtained || !mapLoaded) && submitted &&  (
        <div className="absolute top-0 left-0 text-white h-full w-full flex flex-col space-y-4 items-center z-50 justify-center">
          <LoadingSpinner
            text={
              !positionObtained
                ? "Identifying your position..."
                : loadingStoryData
                ? "Creating Story..."
                : "Loading Visualisation..."
            }
          />
          {!positionObtained && (
            <button onClick={skipPositionObtain}>Skip This Step</button>
          )}
        </div>
      )}
      {children}
    </StoryContext.Provider>
  )
}

export const useStory = () => React.useContext(StoryContext)

export default StoryContext
